import { PathsV1VehicleUuidGetParametersQueryExtend } from "@/api-schema-enums";
import {
  resolveVehicleValuationErrorDescription,
  VEHICLE_VALUATION_CUSTOM_ERRORS,
} from "@/common/api/vehicle-valuation/_helpers/vehicle-valuation-error-description";
import {
  useVehicleValuationCreateMutation,
  type VehicleValuationCreateResponse,
} from "@/common/api/vehicle-valuation/use-vehicle-valuation-create.mutation";
import { useVehicleByUuidQuery } from "@/common/api/vehicle/use-vehicle-by-uuid.query";
import { VehicleWithRegistrationDate } from "@/common/api/vehicle/vehicle.types";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { ErrorPage } from "@/components/error-page/error-page";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { userStore } from "@/store/user-store";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import {
  VehicleValuationCreateFormFields,
  VehicleValuationCreateFormValues,
} from "./_components/vehicle-valuation-create-form-fields";
import { buildVehicleValuationCreateBody } from "./_helpers/vehicle-valuation-create-form.helpers";
import { getVehicleValuationRoute } from "../_helpers/get-vehicle-valuation-route";

export interface VehicleValuationCreateByUuidViewProps {
  uuid: string;
}

export function VehicleValuationCreateByUuidView({ uuid }: VehicleValuationCreateByUuidViewProps) {
  const { t } = useTranslation(["common", "fab", "masterdata"]);
  const [_, navigate] = useLocation();
  const { user } = userStore();

  const {
    data: vehicleData,
    isPending: vehicleIsPending,
    isError: vehicleHasError,
  } = useVehicleByUuidQuery(uuid, {
    parameters: {
      extend: [
        PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration,
        PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration_ModelFamily,
        PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration_ModelFamily_Brand,
        PathsV1VehicleUuidGetParametersQueryExtend.VehicleBlock,
        PathsV1VehicleUuidGetParametersQueryExtend.OwningBranch,
        PathsV1VehicleUuidGetParametersQueryExtend.OwningBranch_Company,
        PathsV1VehicleUuidGetParametersQueryExtend.OwningBranch_Brands,
        PathsV1VehicleUuidGetParametersQueryExtend.OwningBranch_Address,
        PathsV1VehicleUuidGetParametersQueryExtend.CurrentBranch,
        PathsV1VehicleUuidGetParametersQueryExtend.CurrentBranch_Company,
        PathsV1VehicleUuidGetParametersQueryExtend.CurrentBranch_Brands,
        PathsV1VehicleUuidGetParametersQueryExtend.CurrentBranch_Address,
        PathsV1VehicleUuidGetParametersQueryExtend.ExchangeEngine,
      ],
    },
  });

  const vehicle = vehicleData as VehicleWithRegistrationDate | undefined;

  const { isPending: vehicleValuationCreateIsLoading, mutateAsync: vehicleValuationCreateMutateAsync } =
    useVehicleValuationCreateMutation();

  const onSubmit = async (values: VehicleValuationCreateFormValues) => {
    let createdValuation: VehicleValuationCreateResponse | null = null;

    await toastMutationStatus({
      mutationFn: async () => {
        createdValuation = await vehicleValuationCreateMutateAsync(
          buildVehicleValuationCreateBody(values, {
            requester: user.uuid,
            vehicleUuid: uuid,
          }),
        );

        return createdValuation;
      },
      successMessage: t("fab:VehicleValuationCreated"),
      errorMessage: t("fab:ErrorCreatingVehicleValuation"),
      customErrors: VEHICLE_VALUATION_CUSTOM_ERRORS,
      resolveErrorMessage: resolveVehicleValuationErrorDescription,
    });

    if (createdValuation?.uuid) {
      navigate(getVehicleValuationRoute(createdValuation));
    }
  };

  if (!vehicleIsPending && (vehicleHasError || !vehicle)) {
    return (
      <ErrorPage
        title={t("masterdata:VehicleNotFound")}
        message={t("masterdata:VehicleNotFoundForUuid", { uuid })}
        backTo="~/fab/list/initialize/select-vehicle"
      />
    );
  }

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Form}
      title={t("fab:CreateVehicleValuation")}
      subTitle={t("common:CompleteRequiredInformationDescription")}
    >
      <VehicleValuationCreateFormFields
        vehicle={vehicle}
        requireRegistrationDate
        isPending={vehicleIsPending || vehicleValuationCreateIsLoading}
        onCancel={() => navigate("~/fab/list/initialize/select-vehicle")}
        onSubmit={onSubmit}
      />
    </CenteredLayout>
  );
}
